import { useState, type FormEvent } from "react";
import type { BrandingSettings, Health } from "../types";

export type PanelKind = "admin" | "user";

export function LoginPage({
  onLogin,
  onMfa,
  error,
  health,
  mfaPending,
  branding,
  panel = "admin",
  adminPort = 7001,
  userPort = 7002
}: {
  onLogin: (u: string, p: string) => Promise<void>;
  onMfa: (code: string) => Promise<void>;
  error: string;
  health: Health | null;
  mfaPending: boolean;
  branding?: BrandingSettings | null;
  /** Which port this login UI is on (Webuzo-style split) */
  panel?: PanelKind;
  adminPort?: number;
  userPort?: number;
}) {
  const isUserPanel = panel === "user";
  const [username, setUsername] = useState(isUserPanel ? "" : "root");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [busy, setBusy] = useState(false);

  const productName = branding?.productName || "RealPanel";
  const tagline =
    branding?.tagline ||
    (isUserPanel
      ? "Sign in to your hosting account (cPanel)."
      : "WHM admin — root, staff and resellers only.");

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mfaPending) await onMfa(totp);
      else await onLogin(username, password);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-page">
      <form className="login-card" onSubmit={submit}>
        {branding?.logoUrl ? (
          <img
            src={branding.logoUrl}
            alt=""
            style={{
              width: 64,
              height: 64,
              objectFit: "contain",
              marginBottom: 8
            }}
          />
        ) : (
          <div className="brand-mark" aria-hidden>
            <svg viewBox="0 0 64 64" fill="none">
              <path
                d="M14 42V22l10 12 10-12v20"
                stroke="#E8A45C"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle cx="46" cy="24" r="6" stroke="#6EE7B7" strokeWidth="3" />
              <path
                d="M42 38h8M46 34v8"
                stroke="#7C9CFF"
                strokeWidth="3"
                strokeLinecap="round"
              />
            </svg>
          </div>
        )}
        <h1>{productName}</h1>
        <p className="lede">
          {mfaPending
            ? "Enter the 6-digit code from your authenticator app."
            : tagline}
        </p>
        <p
          className="muted"
          style={{
            margin: "0 0 12px",
            fontSize: 12.5,
            fontWeight: 650,
            letterSpacing: "0.02em"
          }}
        >
          {isUserPanel
            ? `User panel · port ${userPort}`
            : `Admin WHM · port ${adminPort}`}
          {" · "}
          <span style={{ fontWeight: 500 }}>
            domain or IP:port both supported
          </span>
        </p>

        {error ? <div className="error-banner">{error}</div> : null}

        {!mfaPending ? (
          <>
            <div className="field">
              <label htmlFor="username">Username</label>
              <input
                id="username"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
              />
            </div>
          </>
        ) : (
          <div className="field">
            <label htmlFor="totp">Authenticator code</label>
            <input
              id="totp"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={totp}
              onChange={(e) => setTotp(e.target.value)}
              placeholder="000000"
              autoFocus
            />
          </div>
        )}

        <button className="btn btn-primary" disabled={busy} type="submit">
          {busy ? "Signing in…" : mfaPending ? "Verify" : "Sign in"}
        </button>

        {branding?.supportUrl ? (
          <p className="muted" style={{ marginTop: 16, fontSize: 13 }}>
            <a href={branding.supportUrl} target="_blank" rel="noreferrer">
              Support
            </a>
          </p>
        ) : null}

        {health ? (
          <p className="muted mono" style={{ marginTop: 12, fontSize: 11 }}>
            {health.service} · {health.status}
          </p>
        ) : null}

        <p className="muted" style={{ marginTop: 14, fontSize: 12, lineHeight: 1.45 }}>
          {isUserPanel ? (
            <>
              Root / reseller login is on the admin panel (port {adminPort}).
            </>
          ) : (
            <>
              Hosting account login is on the user panel (port {userPort}).
            </>
          )}
        </p>
      </form>
    </div>
  );
}
